/**
 * BB Featured Collection Bundle
 * ------------------------------------------------------------
 * Handles per-card option selection, "Add to Cart", and multi-select
 * "Add Bundle to Cart".
 *
 * Cart integration is built to match Shopify's Horizon theme
 * (verified against Shopify/horizon assets/events.js and
 * assets/product-form.js on GitHub, Sep 2026):
 *
 *   - Horizon's cart-drawer-component listens on `document` for a
 *     CustomEvent named "cart:update" (ThemeEvents.cartUpdate /
 *     CartAddEvent.eventName) and opens/refreshes itself from it.
 *   - Horizon's own product-form-component builds that event's detail
 *     as { resource, sourceId, data: { source, itemCount, sections } }.
 *
 * This file dispatches that same event, AND (belt-and-braces, since we
 * can't guarantee every storefront/template has the drawer mounted, or
 * that a given theme's "auto-open cart" setting is turned on) tries a
 * couple of known fallback ways to open the drawer directly, and always
 * shows its own inline "Added to cart" confirmation so an Add to Cart
 * click never appears to silently do nothing.
 *
 * If something fails, check the browser console (errors are logged
 * with the "[BB Bundle]" prefix) and the Network tab for the
 * /cart/add.js request + response.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-bbfcb-section]';
  var CART_ADD_EVENT = 'cart:update';
  var CART_ERROR_EVENT = 'cart:error';
  var LOG_PREFIX = '[BB Bundle] ';

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getCartItemsSectionIds() {
    return Array.prototype.slice
      .call(document.querySelectorAll('cart-items-component'))
      .map(function (el) {
        return el.dataset.sectionId;
      })
      .filter(Boolean);
  }

  function tryOpenCartDrawer() {
    try {
      var drawer = document.querySelector('cart-drawer-component, cart-drawer, [data-cart-drawer]');
      if (!drawer) return false;
      if (typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
      if ('open' in drawer) {
        drawer.open = true;
        return true;
      }
      if (typeof drawer.show === 'function') {
        drawer.show();
        return true;
      }
      drawer.classList.add('active', 'is-open', 'drawer--open');
      drawer.removeAttribute('hidden');
      return true;
    } catch (err) {
      console.error(LOG_PREFIX + 'could not open cart drawer directly', err);
      return false;
    }
  }

  function addItemsToCart(items, sourceId) {
    if (!items || !items.length) {
      return Promise.resolve({ ok: false });
    }

    var sectionIds = getCartItemsSectionIds();

    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: items,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    })
      .then(function (response) {
        return response
          .json()
          .catch(function (err) {
            console.error(LOG_PREFIX + '/cart/add.js did not return valid JSON', err);
            return {};
          })
          .then(function (data) {
            return { response: response, data: data };
          });
      })
      .then(function (result) {
        var response = result.response;
        var data = result.data;

        if (!response.ok || data.status) {
          console.error(LOG_PREFIX + 'cart/add.js failed', response.status, data);
          document.dispatchEvent(
            new CustomEvent(CART_ERROR_EVENT, {
              bubbles: true,
              detail: {
                sourceId: sourceId,
                data: {
                  message: data.message,
                  description: data.description,
                  errors: data.errors,
                },
              },
            })
          );
          return { ok: false, data: data };
        }

        return fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (cartResponse) {
            return cartResponse.json();
          })
          .catch(function (err) {
            console.error(LOG_PREFIX + 'cart.js fetch failed (non-fatal, item was already added)', err);
            return null;
          })
          .then(function (cart) {
            var itemCount = items.reduce(function (sum, item) {
              return sum + (item.quantity || 1);
            }, 0);

            document.dispatchEvent(
              new CustomEvent(CART_ADD_EVENT, {
                bubbles: true,
                detail: {
                  resource: cart || undefined,
                  sourceId: sourceId,
                  data: {
                    source: 'bb-featured-collection-bundle',
                    itemCount: itemCount,
                    sections: data.sections,
                  },
                },
              })
            );

            // Belt-and-braces: don't rely solely on the theme's own
            // listener + its auto-open setting to give the shopper any
            // feedback.
            tryOpenCartDrawer();

            return { ok: true, data: data, cart: cart };
          });
      })
      .catch(function (error) {
        console.error(LOG_PREFIX + 'add to cart request failed entirely (network/CORS/JS error)', error);
        return { ok: false, error: error };
      });
  }

  function setBusy(button, busy) {
    if (!button) return;
    if (busy) {
      button.dataset.bbfcbPrevDisabled = button.disabled ? '1' : '0';
      button.disabled = true;
    } else {
      button.disabled = button.dataset.bbfcbPrevDisabled === '1';
    }
    button.classList.toggle('is-loading', !!busy);
  }

  function showError(root, message) {
    var el = root.querySelector('[data-bbfcb-error]');
    if (!el) return;
    if (!message) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = message;
  }

  function announce(root, message) {
    var live = root.querySelector('[data-bbfcb-live]');
    if (live) live.textContent = message || '';
  }

  var toastTimers = new WeakMap();

  function showToast(root, message) {
    var toast = root.querySelector('[data-bbfcb-toast]');
    if (!toast) return;
    var textEl = toast.querySelector('[data-bbfcb-toast-text]');
    if (textEl) textEl.textContent = message;
    toast.hidden = false;
    toast.classList.add('is-visible');

    var existing = toastTimers.get(toast);
    if (existing) clearTimeout(existing);
    var timer = setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        toast.hidden = true;
      }, 220);
    }, 3500);
    toastTimers.set(toast, timer);
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
      }).format(cents / 100);
    } catch (err) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function withWidth(url, width) {
    if (!url) return url;
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (err) {
      return url;
    }
  }

  function getSelectedOptionValues(card) {
    return Array.prototype.slice
      .call(card.querySelectorAll('[data-bbfcb-option-select]'))
      .sort(function (a, b) {
        return parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10);
      })
      .map(function (select) {
        return select.value;
      });
  }

  function findMatchingVariant(variants, values) {
    return variants.filter(function (v) {
      return Array.isArray(v.options) && v.options.length === values.length;
    }).find(function (v) {
      return v.options.every(function (val, idx) {
        return val === values[idx];
      });
    });
  }

  function updateCardMedia(card, variant) {
    var mediaImg = card.querySelector('.bbfcb-media-img');
    if (!mediaImg || mediaImg.tagName !== 'IMG') return;

    // Cache the card's original (product-level) image the first time we
    // touch it, so we can revert to it for variants that don't have
    // their own dedicated image.
    if (!mediaImg.dataset.bbfcbDefaultSrc) {
      mediaImg.dataset.bbfcbDefaultSrc = mediaImg.currentSrc || mediaImg.getAttribute('src') || '';
      mediaImg.dataset.bbfcbDefaultSrcset = mediaImg.getAttribute('srcset') || '';
      mediaImg.dataset.bbfcbDefaultSizes = mediaImg.getAttribute('sizes') || '';
      mediaImg.dataset.bbfcbDefaultAlt = mediaImg.getAttribute('alt') || '';
    }

    var variantImage = variant && variant.featured_image;

    if (variantImage && variantImage.src) {
      mediaImg.removeAttribute('srcset');
      mediaImg.removeAttribute('sizes');
      mediaImg.src = withWidth(variantImage.src, 800);
      if (variantImage.alt) mediaImg.alt = variantImage.alt;
    } else {
      mediaImg.src = mediaImg.dataset.bbfcbDefaultSrc;
      if (mediaImg.dataset.bbfcbDefaultSrcset) mediaImg.setAttribute('srcset', mediaImg.dataset.bbfcbDefaultSrcset);
      if (mediaImg.dataset.bbfcbDefaultSizes) mediaImg.setAttribute('sizes', mediaImg.dataset.bbfcbDefaultSizes);
      mediaImg.alt = mediaImg.dataset.bbfcbDefaultAlt;
    }
  }

  function refreshCardVariant(card, onChange) {
    var variantsScript = card.querySelector('[data-bbfcb-variants]');
    var quickAdd = card.querySelector('[data-bbfcb-quick-add]');
    var priceEl = card.querySelector('[data-bbfcb-price]');
    var comparePriceEl = card.querySelector('[data-bbfcb-compare-price]');
    var unavailableEl = card.querySelector('[data-bbfcb-unavailable]');

    // No option selectors on this card (single-variant product) —
    // the variant id set in Liquid is already correct and static, and
    // there's only one image to show anyway.
    if (!variantsScript) return;

    var variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (err) {
      console.error(LOG_PREFIX + 'could not parse embedded variant JSON', err);
      return;
    }

    var values = getSelectedOptionValues(card);
    var match = findMatchingVariant(variants, values);
    var label = quickAdd ? quickAdd.querySelector('.bbfcb-btn-label') : null;

    if (match && match.available) {
      card.dataset.variantId = match.id;
      card.classList.remove('is-sold-out');
      if (unavailableEl) unavailableEl.hidden = true;
      if (quickAdd) {
        quickAdd.dataset.variantId = match.id;
        quickAdd.disabled = false;
        if (label) label.textContent = quickAdd.dataset.labelDefault || label.textContent;
      }
      if (priceEl) priceEl.textContent = formatMoney(match.price);
      if (comparePriceEl) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          comparePriceEl.textContent = formatMoney(match.compare_at_price);
          comparePriceEl.hidden = false;
        } else {
          comparePriceEl.hidden = true;
        }
      }
      updateCardMedia(card, match);
    } else {
      delete card.dataset.variantId;
      card.classList.add('is-sold-out');
      if (unavailableEl) unavailableEl.hidden = false;
      if (quickAdd) {
        quickAdd.dataset.variantId = '';
        quickAdd.disabled = true;
        if (label) label.textContent = match ? quickAdd.dataset.labelSoldout : quickAdd.dataset.labelUnavailable;
      }
      // Sold out ≠ no such variant: a sold-out color should still show
      // that color's photo, same as a normal product page.
      if (match) updateCardMedia(card, match);
    }

    if (typeof onChange === 'function') onChange(card);
  }

  function initSection(root) {
    if (root.dataset.bbfcbInit === '1') return;
    root.dataset.bbfcbInit = '1';

    var sectionId = root.id || 'bbfcb';
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-bbfcb-card]'));
    var bar = root.querySelector('[data-bbfcb-bar]');
    var countEl = root.querySelector('[data-bbfcb-count]');
    var hintEl = root.querySelector('[data-bbfcb-hint]');
    var bundleAddBtn = root.querySelector('[data-bbfcb-bundle-add]');
    var minItems = parseInt(root.dataset.bbfcbMinItems || '2', 10) || 2;

    var tierEls = Array.prototype.slice
      .call(root.querySelectorAll('[data-bbfcb-tier]'))
      .map(function (el) {
        var labelEl = el.querySelector('.bbfcb-tier-label');
        return {
          qty: parseInt(el.dataset.tierQty, 10) || 0,
          label: labelEl ? labelEl.textContent.trim() : '',
        };
      })
      .sort(function (a, b) {
        return a.qty - b.qty;
      });

    function selectedCards() {
      return cards.filter(function (card) {
        return card.classList.contains('is-selected');
      });
    }

    function updateBar() {
      if (!bar) return;
      var selected = selectedCards();
      var count = selected.length;

      if (countEl) countEl.textContent = String(count);
      bar.hidden = count === 0;

      if (bundleAddBtn) {
        bundleAddBtn.disabled = count < minItems;
      }

      if (hintEl) {
        if (count === 0) {
          hintEl.textContent = hintEl.dataset.defaultText || hintEl.textContent;
        } else if (tierEls.length) {
          var reached = null;
          for (var i = 0; i < tierEls.length; i++) {
            if (count >= tierEls[i].qty) reached = tierEls[i];
          }
          var next = tierEls.filter(function (t) {
            return t.qty > count;
          })[0];

          if (reached && !next) {
            hintEl.textContent = "You've unlocked " + reached.label + '!';
          } else if (reached && next) {
            hintEl.textContent = "You've unlocked " + reached.label + '. Add ' + (next.qty - count) + ' more for ' + next.label + '.';
          } else if (next) {
            hintEl.textContent = 'Add ' + (next.qty - count) + ' more to unlock ' + next.label + '.';
          }
        }
      }
    }

    function deselectIfUnavailable(card) {
      if (card.classList.contains('is-selected') && card.classList.contains('is-sold-out')) {
        card.classList.remove('is-selected');
        var toggle = card.querySelector('[data-bbfcb-select]');
        if (toggle) toggle.setAttribute('aria-pressed', 'false');
        updateBar();
      }
    }

    cards.forEach(function (card) {
      // Initialize price/variant state from whatever options are
      // selected by default (Liquid pre-selects the option values that
      // match the product's first available variant).
      refreshCardVariant(card);

      var optionSelects = card.querySelectorAll('[data-bbfcb-option-select]');
      optionSelects.forEach(function (select) {
        select.addEventListener('change', function () {
          refreshCardVariant(card, deselectIfUnavailable);
        });
      });

      var toggle = card.querySelector('[data-bbfcb-select]');
      if (toggle) {
        toggle.addEventListener('click', function () {
          if (card.classList.contains('is-sold-out')) return;
          var nowSelected = card.classList.toggle('is-selected');
          toggle.setAttribute('aria-pressed', String(nowSelected));
          updateBar();
        });
      }

      var quickAdd = card.querySelector('[data-bbfcb-quick-add]');
      if (quickAdd) {
        quickAdd.addEventListener('click', function () {
          var variantId = quickAdd.dataset.variantId;
          if (!variantId) return;
          showError(root, '');
          setBusy(quickAdd, true);
          addItemsToCart([{ id: variantId, quantity: 1 }], sectionId + '-quick-add').then(function (result) {
            setBusy(quickAdd, false);
            if (!result.ok) {
              showError(root, (result.data && result.data.message) || 'Something went wrong adding this item to your cart.');
            } else {
              announce(root, 'Added to cart');
              showToast(root, 'Added to cart');
            }
          });
        });
      }
    });

    if (bundleAddBtn) {
      bundleAddBtn.addEventListener('click', function () {
        var selected = selectedCards();
        if (selected.length < minItems) return;

        var items = selected
          .map(function (card) {
            return { id: card.dataset.variantId, quantity: 1 };
          })
          .filter(function (item) {
            return item.id;
          });

        if (!items.length) return;

        showError(root, '');
        setBusy(bundleAddBtn, true);

        addItemsToCart(items, sectionId + '-bundle-add').then(function (result) {
          setBusy(bundleAddBtn, false);
          if (!result.ok) {
            showError(root, (result.data && result.data.message) || 'Something went wrong adding your bundle to the cart.');
            return;
          }
          announce(root, 'Bundle added to cart');
          showToast(root, items.length + ' item(s) added to cart');
          selected.forEach(function (card) {
            card.classList.remove('is-selected');
            var toggle = card.querySelector('[data-bbfcb-select]');
            if (toggle) toggle.setAttribute('aria-pressed', 'false');
          });
          updateBar();
        });
      });
    }

    updateBar();
  }

  function init() {
    Array.prototype.slice.call(document.querySelectorAll(ROOT_SELECTOR)).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the theme editor swaps this section's markup in place.
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.querySelectorAll) return;
    var root = event.target.matches && event.target.matches(ROOT_SELECTOR) ? event.target : event.target.querySelector(ROOT_SELECTOR);
    if (root) {
      root.dataset.bbfcbInit = '0';
      initSection(root);
    }
  });
})();